import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { API } from "aws-amplify";
import 'moment/locale/es';
import moment from 'moment';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faTicketAlt, faPlus, faMoneyBillAlt, faInfoCircle, faArrowRight, faShoppingCart } from '@fortawesome/free-solid-svg-icons'
import { Form, Row, Col, Button, Badge, Modal } from "react-bootstrap";
import LoaderButton from '../LoaderButton';

import { useAnalytics } from "libs/analytics";
import { useCart } from "libs/cartLib";
import { useAppContext } from "libs/contextLib";
import { Notification } from "libs/errorLib";
import Loader from 'components/Loader'
import Moment from "react-moment";
import { faSlideshare } from "@fortawesome/free-brands-svg-icons";
// import { useOrder } from "libs/ordersLib";
import './ShowMapTickets.css'

function pad(num, size) {
    var s = "000000000" + num;
    return s.substr(s.length-size);
}

const row_names = ['A','B','C','D','E','F','G','H','I','J','K','L','M','N','O','P','Q','R','S','T','U','V',/*'W',*/'X','Y','Z','AA','BB','CC','DD','EE','FF','GG','HH','II','JJ','KK','LL','MM','NN','OO','PP','QQ','RR','SS','TT','UU','VV','XX','YY','ZZ'];

// --- Utility Functions for Seat Rendering ---

/**
 * Creates a seat element with unified rendering logic
 * @param {Object} seat - The seat object
 * @param {Object} section - The section object
 * @param {Object} handlers - Event handlers object
 * @param {Object} options - Additional options
 * @returns {JSX.Element} The rendered seat element
 */
function createSeatElement(seat, section, handlers, options = {}) {
    const { readOnly = false } = options;
    
    if (seat.waiting) {
        return (
            <div 
                className="seat loading" 
                data-section-id={section._id} 
                data-seat-id={seat._id}
                onMouseEnter={(e) => handlers.onMouseEnter(e, seat, section)}
                onMouseLeave={handlers.onMouseLeave}
            />
        );
    }
    
    if (seat.available) {
        return (
            <div 
                className={`seat available ${readOnly ? 'readonly' : ''}`} 
                onClick={(e) => handlers.onClick(seat, true)} 
                data-section-id={section._id} 
                data-seat-id={seat._id}
                onMouseEnter={(e) => handlers.onMouseEnter(e, seat, section)}
                onMouseLeave={handlers.onMouseLeave}
            />
        );
    }
    
    if (seat.reserved) {
        return (
            <div 
                className="seat reserved" 
                onClick={(e) => handlers.onClick(seat, false)} 
                data-section-id={section._id} 
                data-seat-id={seat._id}
                onMouseEnter={(e) => handlers.onMouseEnter(e, seat, section)}
                onMouseLeave={handlers.onMouseLeave}
            />
        );
    }
    
    return (
        <div 
            className="seat sold" 
            data-section-id={section._id} 
            data-seat-id={seat._id}
            onMouseEnter={(e) => handlers.onMouseEnter(e, seat, section)}
            onMouseLeave={handlers.onMouseLeave}
        />
    );
}

/**
 * Generates seat name based on row and column coordinates
 * @param {number} row - Row index
 * @param {number} col - Column index
 * @param {Object} section - Section configuration
 * @returns {string} The generated seat name
 */
function generateSeatName(row, col, section) {
    const seat_padding = 2;
    const row_name_index = section.row_start + (row * section.row_step);
    const col_number_index = section.col_start + (col * section.col_step);
    
    const row_name = row_names[row_name_index];
    const number = col_number_index;
    const name = row_name + pad(number, seat_padding);
    
    return name;
}

/**
 * Calculates grid dimensions for a section
 * @param {Object} section - Section configuration
 * @param {boolean} showNumbering - Whether to show seat numbering
 * @returns {Object} Object with total_rows and total_cols
 */
function calculateGridDimensions(section, showNumbering) {
    const total_rows = showNumbering ? section.map_rows + 2 : section.map_rows; // +2 for top/bottom numbering rows
    const total_cols = showNumbering ? section.map_columns + 2 : section.map_columns; // +2 for left/right numbering columns
    
    return { total_rows, total_cols };
}

/**
 * Creates a grid container with specified dimensions
 * @param {Array} gridCells - Array of grid cell elements
 * @param {number} totalCols - Total number of columns
 * @param {number} totalRows - Total number of rows
 * @param {string} className - CSS class name for the grid
 * @returns {JSX.Element} The grid container
 */
function createGridContainer(gridCells, totalCols, totalRows, className = "table_grid") {
    return (
        <div className={className} style={{
            display: 'grid',
            gridTemplateColumns: `repeat(${totalCols}, 25px)`,
            gridTemplateRows: `repeat(${totalRows}, 25px)`,
            gap: '0px'
        }}>
            {gridCells}
        </div>
    );
}

/**
 * Creates a grid container with numbering support
 * @param {Array} gridCells - Array of grid cell elements
 * @param {number} mapColumns - Number of map columns (without numbering)
 * @param {number} mapRows - Number of map rows (without numbering)
 * @param {boolean} showNumbering - Whether to show seat numbering
 * @param {string} className - CSS class name for the grid
 * @returns {JSX.Element} The grid container with numbering
 */
function createNumberedGridContainer(gridCells, mapColumns, mapRows, showNumbering, className = "table_grid") {
    if (!showNumbering) {
        return createGridContainer(gridCells, mapColumns, mapRows, className);
    }
    
    return (
        <div className={className} style={{
            display: 'grid',
            gridTemplateColumns: `25px repeat(${mapColumns}, 25px) 25px`,
            gridTemplateRows: `repeat(${mapRows}, 25px)`,
            gap: '0px'
        }}>
            {gridCells}
        </div>
    );
}

/**
 * Creates a grid container for seats with left numbering only
 * @param {Array} gridCells - Array of grid cell elements
 * @param {number} mapColumns - Number of map columns
 * @param {number} mapRows - Number of map rows
 * @param {boolean} showNumbering - Whether to show seat numbering
 * @param {string} className - CSS class name for the grid
 * @returns {JSX.Element} The grid container with left numbering
 */
function createLeftNumberedGridContainer(gridCells, mapColumns, mapRows, showNumbering, className = "table_grid") {
    if (!showNumbering) {
        return createGridContainer(gridCells, mapColumns, mapRows, className);
    }
    
    return (
        <div className={className} style={{
            display: 'grid',
            gridTemplateColumns: `25px repeat(${mapColumns}, 25px)`,
            gridTemplateRows: `repeat(${mapRows}, 25px)`,
            gap: '0px'
        }}>
            {gridCells}
        </div>
    );
}

// --- Section Type Registry System ---

/**
 * Section type registry for centralized section rendering
 */
const SECTION_RENDERERS = {
    'SEATS': null, // Will be defined in the component
    'TABLE': null, // Will be defined in the component
    'HTABLE': null, // Will be defined in the component
    'OPEN': null // Will be defined in the component
};

/**
 * Renders a section by its type using the registry
 * @param {Object} section - The section object
 * @param {Object} options - Rendering options
 * @param {Object} context - Component context (this component's state and methods)
 * @returns {JSX.Element} The rendered section
 */
function renderSectionByType(section, options, context) {
    const renderer = SECTION_RENDERERS[section.type];
    if (!renderer) {
        console.warn(`No renderer found for section type: ${section.type}`);
        return renderDefaultSection(section, options, context);
    }
    const result = renderer(section, options, context);
    return result;
}

/**
 * Default section renderer for unknown section types
 * @param {Object} section - The section object
 * @param {Object} options - Rendering options
 * @param {Object} context - Component context
 * @returns {JSX.Element} Default rendered section
 */
function renderDefaultSection(section, options, context) {
    console.warn(`Using default renderer for section type: ${section.type}`);
    return (
        <div className="venue_map">
            <div className="venue_section default">
                <div className="section-content">
                    <div className="section-body">
                        <div className="default-section-message">
                            Tipo de sección no soportado: {section.type}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * Checks if a section type is supported
 * @param {string} sectionType - The section type to check
 * @returns {boolean} True if the section type is supported
 */
function isSectionTypeSupported(sectionType) {
    return SECTION_RENDERERS.hasOwnProperty(sectionType);
}

/**
 * Gets all supported section types
 * @returns {Array} Array of supported section type names
 */
function getSupportedSectionTypes() {
    return Object.keys(SECTION_RENDERERS);
}


export default function ShowMapTickets({ show, showTickets, showMap, readOnly=false, paymentType, showViewCart = true,  ...props}) {
    // const { show, viewerShow, notifyTicketChange, showAddToCart: showAddToCart = true, 
    //     quickBuyLink: quickBuyLink = false, ...otherProps } = props

    const history = useHistory();
    const [tickets, setTickets] = useState(null)
    const [totalTickets, setTotalTickets] = useState(0)
    const [orderTotal, setOrderTotal] = useState(0);
    const [orderDiscountTotal, setOrderDiscountTotal] = useState(0);
    const [currency, setCurrency] = useState(show?.settings?.currency || 'GTQ')

    const [selectedLocation, setSelectedLocation] = useState(null)
    const [selectedTicket, setSelectedTicket] = useState(null)
    const [selectedSection, setSelectedSection] = useState(null)
    const [showMapSeats, setShowMapSeats] = useState();
    const [sectionSeats, setSectionSeats] = useState({}); // Store seats by section ID
    const [selectedSeats, setSelectedSeats] = useState([])
    const [openTicketQuantity, setOpenTicketQuantity] = useState(0)
    const [sectionQuantities, setSectionQuantities] = useState({})

    const [mapLoading, setMapLoading] = useState(false)
    const [isLoading, setIsLoading] = useState(false);

    // Tooltip state
    const [tooltipState, setTooltipState] = useState({
        isVisible: false,
        seat: null,
        section: null,
        position: { x: 0, y: 0 }
    })

    const { trackEvent } = useAnalytics();
    // const [order, setOrder] = useOrder(show._id)
    const { getCart, addCartItems, removeCartItems, removeCartSeats } = useCart()
    const { updateCartCounter } = useAppContext()

    // Check if this is for registration/free tickets
    const isRegistrationTicket = paymentType === 'REGISTRATION' || (showTickets && showTickets.length > 0 && showTickets[0].payment_type && showTickets[0].payment_type.includes('REGISTRATION'))
    
    // --- Section Renderer Definitions ---
    // Define section renderers with access to component state and methods
    
    const sectionRenderers = {
        'SEATS': (section, options, context) => {
            const { inGrid = false, readOnly = false } = options;
            const { sectionSeats, showMapSeats, onSeatClick, handleSeatMouseEnter, handleSeatMouseLeave } = context;
            
            const sectionSeatsList = sectionSeats[section._id] || showMapSeats || [];
            
            if (sectionSeatsList.length === 0) {
                return inGrid ? <Loader /> : (
                    <div className="venue_map">
                        <div className="venue_section seats">
                            <Loader />
                        </div>
                    </div>
                );
            }
            
            const grid_cells = [];
            const show_numbering = section.show_numbering;
            
            // Generate grid cells
            for (let row = 0, row_name_index = section.row_start; row < section.map_rows; row++, row_name_index += section.row_step) {
                const row_name = row_names[row_name_index];
                
                // Add row name on the left if numbering is enabled
                if (show_numbering) {
                    grid_cells.push(<div key={`row-${row}`} className="grid_cell numbering_cell">{row_name}</div>);
                }
                
                for (let col = 0, col_number_index = section.col_start; col < section.map_columns; col++, col_number_index += section.col_step) {
                    const seat = sectionSeatsList.find(s => s.row === row && s.col === col);
                    
                    if (seat) {
                        const seatElement = createSeatElement(seat, section, {
                            onClick: onSeatClick,
                            onMouseEnter: handleSeatMouseEnter,
                            onMouseLeave: handleSeatMouseLeave
                        }, { readOnly });
                        
                        grid_cells.push(<div key={`seat-${section._id}-${row}-${col}`} className="grid_cell">{seatElement}</div>);
                    } else {
                        // Empty seat position
                        grid_cells.push(<div key={`empty-${section._id}-${row}-${col}`} className="grid_cell"><div></div></div>);
                    }
                }
            }
            
            const tableGrid = createLeftNumberedGridContainer(
                grid_cells, 
                section.map_columns, 
                section.map_rows, 
                show_numbering
            );
            
            if (inGrid) {
                return tableGrid;
            } else {
                return (
                    <div className="venue_map">
                        <div className="venue_section seats">
                            {tableGrid}
                        </div>
                        {renderVenueLegend()}
                    </div>
                );
            }
        },
        
        'TABLE': (section, options, context) => {
            const { inGrid = false, readOnly = false } = options;
            const { sectionSeats, showMapSeats, onSeatClick, handleSeatMouseEnter, handleSeatMouseLeave } = context;
            
            const sectionSeatsList = sectionSeats[section._id] || showMapSeats || [];
            
            if (sectionSeatsList.length === 0) {
                return inGrid ? <Loader /> : (
                    <div className="venue_map">
                        <div className="venue_section table">
                            <Loader />
                        </div>
                    </div>
                );
            }
            
            const grid_cells = [];
            const show_numbering = section.show_numbering;
            
            // Generate grid cells
            for (let row = 0, row_name_index = section.row_start; row < section.map_rows; row++, row_name_index += section.row_step) {
                const row_name = row_names[row_name_index];
                
                for (let col = 0, col_number_index = section.col_start; col < section.map_columns; col++, col_number_index += section.col_step) {
                    const seat = sectionSeatsList.find(s => s.row === row && s.col === col);
                    
                    if (seat) {
                        const seatElement = createSeatElement(seat, section, {
                            onClick: onSeatClick,
                            onMouseEnter: handleSeatMouseEnter,
                            onMouseLeave: handleSeatMouseLeave
                        }, { readOnly });
                        
                        // Add seat to grid
                        if (show_numbering) {
                            // Add left numbering if it's the first column
                            if (col === 0) {
                                grid_cells.push(<div key={`left-${section._id}-${row}`} className="grid_cell numbering_cell">{seat.name}</div>);
                            }
                            
                            // Add seat
                            grid_cells.push(<div key={`seat-${section._id}-${row}-${col}`} className="grid_cell">{seatElement}</div>);
                            
                            // Add right numbering if it's the last column
                            if (col === section.map_columns - 1) {
                                grid_cells.push(<div key={`right-${section._id}-${row}`} className="grid_cell numbering_cell">{seat.name}</div>);
                            }
                        } else {
                            // No numbering, just add seat
                            grid_cells.push(<div key={`seat-${section._id}-${row}-${col}`} className="grid_cell">{seatElement}</div>);
                        }
                    } else {
                        // Check if this position is a corner where a seat could exist
                        const isCorner = (row === 0 || row === section.map_rows - 1) && 
                                       (col === 0 || col === section.map_columns - 1);
                        
                        if (isCorner) {
                            // Corner position - empty space
                            if (show_numbering) {
                                if (col === 0) {
                                    grid_cells.push(<div key={`left-${section._id}-${row}`} className="grid_cell numbering_cell"></div>);
                                }
                                grid_cells.push(<div key={`corner-${section._id}-${row}-${col}`} className="grid_cell"><div style={{width: 20, height: 20, margin: 2}}></div></div>);
                                if (col === section.map_columns - 1) {
                                    grid_cells.push(<div key={`right-${section._id}-${row}`} className="grid_cell numbering_cell"></div>);
                                }
                            } else {
                                grid_cells.push(<div key={`corner-${section._id}-${row}-${col}`} className="grid_cell"><div style={{width: 20, height: 20, margin: 2}}></div></div>);
                            }
                        } else {
                            // Non-corner position with no seat - add gray placeholder
                            if (show_numbering) {
                                if (col === 0) {
                                    grid_cells.push(<div key={`left-${section._id}-${row}`} className="grid_cell numbering_cell"></div>);
                                }
                                grid_cells.push(<div key={`gray-${section._id}-${row}-${col}`} className="grid_cell"><div style={{background:'gray', height: 25, width: 25, margin: 0}}></div></div>);
                                if (col === section.map_columns - 1) {
                                    grid_cells.push(<div key={`right-${section._id}-${row}`} className="grid_cell numbering_cell"></div>);
                                }
                            } else {
                                grid_cells.push(<div key={`gray-${section._id}-${row}-${col}`} className="grid_cell"><div style={{background:'gray', height: 25, width: 25, margin: 0}}></div></div>);
                            }
                        }
                    }
                }
            }
            
            const tableGrid = createNumberedGridContainer(
                grid_cells, 
                section.map_columns, 
                section.map_rows, 
                show_numbering
            );
            
            if (inGrid) {
                return tableGrid;
            } else {
                return (
                    <div className="venue_map">
                        <div className="venue_section table">
                            {tableGrid}
                        </div>
                        {renderVenueLegend()}
                    </div>
                );
            }
        },
        
        'HTABLE': (section, options, context) => {
            const { inGrid = false, readOnly = false } = options;
            const { sectionSeats, showMapSeats, onSeatClick, handleSeatMouseEnter, handleSeatMouseLeave } = context;
            
            const sectionSeatsList = sectionSeats[section._id] || showMapSeats || [];
            
            if (sectionSeatsList.length === 0) {
                return inGrid ? <Loader /> : (
                    <div className="venue_map">
                        <div className="venue_section htable">
                            <Loader />
                        </div>
                    </div>
                );
            }
            
            const grid_cells = [];
            const show_numbering = section.show_numbering;
            const { total_rows, total_cols } = calculateGridDimensions(section, show_numbering);
            
            // Generate grid cells row by row
            for (let grid_row = 0; grid_row < total_rows; grid_row++) {
                for (let grid_col = 0; grid_col < total_cols; grid_col++) {
                    const key = `${section._id}-${grid_row}-${grid_col}`;
                    
                    if (show_numbering) {
                        // Handle numbering cells
                        if (grid_row === 0) {
                            // Top numbering row
                            if (grid_col === 0 || grid_col === total_cols - 1) {
                                // Corner cells - empty
                                grid_cells.push(<div key={key} className="grid_cell"></div>);
                            } else {
                                // Top numbering for seats in first row
                                const seat_col = grid_col - 1;
                                const seat = sectionSeatsList.find(s => s.row === 0 && s.col === seat_col);
                                if (seat) {
                                    grid_cells.push(<div key={key} className="grid_cell numbering_cell">{seat.name}</div>);
                                } else {
                                    grid_cells.push(<div key={key} className="grid_cell"></div>);
                                }
                            }
                        } else if (grid_row === total_rows - 1) {
                            // Bottom numbering row
                            if (grid_col === 0 || grid_col === total_cols - 1) {
                                // Corner cells - empty
                                grid_cells.push(<div key={key} className="grid_cell"></div>);
                            } else {
                                // Bottom numbering for seats in last row
                                const seat_col = grid_col - 1;
                                const seat = sectionSeatsList.find(s => s.row === section.map_rows - 1 && s.col === seat_col);
                                if (seat) {
                                    grid_cells.push(<div key={key} className="grid_cell numbering_cell">{seat.name}</div>);
                                } else {
                                    grid_cells.push(<div key={key} className="grid_cell"></div>);
                                }
                            }
                        } else {
                            // Middle rows (actual seat rows)
                            const seat_row = grid_row - 1;
                            
                            if (grid_col === 0) {
                                // Left numbering column
                                const seat = sectionSeatsList.find(s => s.row === seat_row && s.col === 0);
                                if (seat) {
                                    grid_cells.push(<div key={key} className="grid_cell numbering_cell">{seat.name}</div>);
                                } else {
                                    grid_cells.push(<div key={key} className="grid_cell"></div>);
                                }
                            } else if (grid_col === total_cols - 1) {
                                // Right numbering column
                                const seat = sectionSeatsList.find(s => s.row === seat_row && s.col === section.map_columns - 1);
                                if (seat) {
                                    grid_cells.push(<div key={key} className="grid_cell numbering_cell">{seat.name}</div>);
                                } else {
                                    grid_cells.push(<div key={key} className="grid_cell"></div>);
                                }
                            } else {
                                // Seat cell
                                const seat_col = grid_col - 1;
                                const seat = sectionSeatsList.find(s => s.row === seat_row && s.col === seat_col);
                                
                                if (seat) {
                                    const seatElement = createSeatElement(seat, section, {
                                        onClick: onSeatClick,
                                        onMouseEnter: handleSeatMouseEnter,
                                        onMouseLeave: handleSeatMouseLeave
                                    }, { readOnly });
                                    
                                    grid_cells.push(<div key={key} className="grid_cell">{seatElement}</div>);
                                } else {
                                    // Check if this position is a corner where a seat could exist
                                    const isCorner = (seat_row === 0 || seat_row === section.map_rows - 1) && 
                                                   (seat_col === 0 || seat_col === section.map_columns - 1);
                                    
                                    if (isCorner) {
                                        // Corner position - empty space
                                        grid_cells.push(<div key={key} className="grid_cell"><div style={{width: 20, height: 20, margin: 2}}></div></div>);
                                    } else {
                                        // Non-corner position with no seat - add gray placeholder
                                        grid_cells.push(<div key={key} className="grid_cell"><div style={{background:'gray', height: 25, width: 25, margin: 0}}></div></div>);
                                    }
                                }
                            }
                        }
                    } else {
                        // No numbering - simple grid
                        const seat = sectionSeatsList.find(s => s.row === grid_row && s.col === grid_col);
                        
                        if (seat) {
                            const seatElement = createSeatElement(seat, section, {
                                onClick: onSeatClick,
                                onMouseEnter: handleSeatMouseEnter,
                                onMouseLeave: handleSeatMouseLeave
                            }, { readOnly });
                            
                            grid_cells.push(<div key={key} className="grid_cell">{seatElement}</div>);
                        } else {
                            // Check if this position is a corner where a seat could exist
                            const isCorner = (grid_row === 0 || grid_row === section.map_rows - 1) && 
                                           (grid_col === 0 || grid_col === section.map_columns - 1);
                            
                            if (isCorner) {
                                // Corner position - empty space
                                grid_cells.push(<div key={key} className="grid_cell"><div style={{width: 20, height: 20, margin: 2}}></div></div>);
                            } else {
                                // Non-corner position with no seat - add gray placeholder
                                grid_cells.push(<div key={key} className="grid_cell"><div style={{background:'gray', height: 25, width: 25, margin: 0}}></div></div>);
                            }
                        }
                    }
                }
            }
            
            const tableGrid = createGridContainer(grid_cells, total_cols, total_rows);
            
            if (inGrid) {
                return tableGrid;
            } else {
                return (
                    <div className="venue_map">
                        <div className="venue_section htable">
                            {tableGrid}
                        </div>
                        {renderVenueLegend()}
                    </div>
                );
            }
        },
        
        'OPEN': (section, options, context) => {
            const { inGrid = false, readOnly = false } = options;
            const { 
                showTickets, 
                selectedTicket, 
                openTicketQuantity, 
                handleOpenTicketSelection, 
                handleAddToCartButton, 
                validateOpenTicketSelection,
                sectionQuantities,
                handleSectionOpenTicketSelection,
                handleSectionAddToCartButton,
                validateSectionOpenTicketSelection
            } = context;
            
            // Find the ticket for this specific section
            let t = null;
            if (inGrid) {
                // In grid mode, find ticket based on the section's location
                if (selectedLocation && selectedLocation.show_tickets && selectedLocation.show_tickets.length > 0) {
                    // Use the first ticket from the location's show_tickets array
                    const ticketId = selectedLocation.show_tickets[0];
                    t = showTickets.find(ticket => ticket._id === ticketId);
                }
            } else {
                // In regular mode, use the selected ticket
                t = showTickets.find(ticket => ticket._id === selectedTicket._id);
            }
            
            // Safety check - if no ticket found, return empty content
            if (!t) {
                console.error('No ticket found for section:', section);
                return <div className="venue_section open">No ticket available</div>;
            }
            
            const now = moment.utc();
            const since = moment.utc(t.valid_since);
            const thru = t.valid_thru ? moment.utc(t.valid_thru) : now;
            
            t.since = since;
            t.thru = thru;
            t.expired = now.isAfter(thru);
            t.can_buy = now.isSameOrAfter(since);
            
            let quantityOptions = [];
            for (let i = 0; i <= 10 && i <= t.real_available; i++) {
                quantityOptions.push(<option value={`${i}`} key={`${t.ticket_id}-${i}`}>{`${i}`}</option>);
            }
            
            let buy = '';
            if (t.expired) {
                buy = <span><Badge variant="secondary">No Disponible</Badge></span>;
            } else if (t.can_buy) {
                buy = t.real_available > 0 
                    ? <div className="selection">Cantidad: <Form.Control as="select" value={openTicketQuantity} onChange={(e) => handleOpenTicketSelection(e, t)} >
                        { quantityOptions }                              
                    </Form.Control></div>
                    : <Badge variant="secondary">AGOTADOS</Badge>;
            } else {
                buy = <span className="presale-date">A partir del <Moment local format="lll">{t.since}</Moment></span>;
            }
            
            const openContent = (
                <div className="venue_section open">
                    <div>
                    {section.seated && <div style={{textAlign:'center'}}>** Asientos no numerados **</div>}
                    {!readOnly && (
                        <>
                            {buy}
                            <Button
                                disabled={!validateOpenTicketSelection()}
                                onClick={handleAddToCartButton}
                            >
                                <FontAwesomeIcon icon={faPlus} /> Agregar al Carrito
                            </Button>
                        </>
                    )}
                    </div>
                </div>
            );

            const gridContent = (
                <div className="venue_section open grid_open_section">
                    <div className="section_header">
                        <h4 className="section_name">{section.name}</h4>
                        <p className="section_type">** Asientos no numerados **</p>
                    </div>
                    <div className="section_content">
                        {!readOnly && (
                            <>
                                <div className="quantity_selector">
                                    <label className="quantity_label">Cantidad:</label>
                                    <select
                                        value={sectionQuantities[section._id] || 0}
                                        onChange={(e) => handleSectionOpenTicketSelection(section._id, e, t)}
                                        disabled={readOnly}
                                        className="quantity_select"
                                    >
                                        {quantityOptions}
                                    </select>
                                </div>
                                <div className="button_container">
                                    <Button
                                        className="grid_add_button"
                                        disabled={!validateSectionOpenTicketSelection(section._id)}
                                        onClick={() => handleSectionAddToCartButton(section._id, t)}
                                        size="sm"
                                    >
                                        + Agregar
                                    </Button>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            );
            
            if (inGrid) {
                return gridContent;
            } else {
                return (
                    <div className="venue_map">
                        {openContent}
                    </div>
                );
            }
        }
    };
    
    // Update the global SECTION_RENDERERS with the component-specific renderers
    Object.keys(sectionRenderers).forEach(type => {
        SECTION_RENDERERS[type] = sectionRenderers[type];
    });

    useEffect( () => {
        console.log('useEffect triggered for calculateTotals:', { selectedSeats: selectedSeats.length, openTicketQuantity, sectionQuantities });
        if (!readOnly) {
            calculateTotals()
        }
        // setTickets(tickets)
        // setOrderTotal(0)
        // setTotalTickets(0)
        
        // if (notifyTicketChange) {
        //     notifyTicketChange(null, 0, 0, currency)
        // }
    }, [selectedSeats, openTicketQuantity, sectionQuantities])

    useEffect( () => {
        try {
            setMapLoading(true)
            setSelectedSeats([])
            setOpenTicketQuantity(0)

            if (show && selectedLocation) {
                // Check if this location should use grid layout
                const hasGridConfig = selectedLocation.section_grid_rows && selectedLocation.section_grid_cols;
                const sectionsWithGridCoords = selectedLocation.sections.filter(s => 
                    s.section_grid_row !== undefined && s.section_grid_col !== undefined
                );
                const shouldUseGrid = hasGridConfig && sectionsWithGridCoords.length > 1;
                
                if (shouldUseGrid) {
                    // Load all seats for grid layout - don't set mapLoading to false here
                    loadAllSectionSeats(show._id, selectedLocation._id)
            } else {
                    // For single section or regular layouts, clear seats until section is selected
                setShowMapSeats([])
                    setSectionSeats({})
                    setMapLoading(false) // Only set to false for non-grid layouts
                }
            } else {
                setShowMapSeats([])
                setSectionSeats({})
                setMapLoading(false)
            }            
        } catch (error) {
            console.error('Error in location selection:', error)
            setMapLoading(false)
        }
    }, [selectedLocation])

    useEffect( () => {
        try {
            setMapLoading(true)
            setSelectedSeats([])
            setOpenTicketQuantity(0)

            if (show && selectedLocation && selectedSection) {
                // Check if this location should use grid layout
                const hasGridConfig = selectedLocation.section_grid_rows && selectedLocation.section_grid_cols;
                const sectionsWithGridCoords = selectedLocation.sections.filter(s => 
                    s.section_grid_row !== undefined && s.section_grid_col !== undefined
                );
                const shouldUseGrid = hasGridConfig && sectionsWithGridCoords.length > 1;
                
                if (!shouldUseGrid) {
                    // Load seats for single section
                    loadShowMapSeats(show._id, selectedLocation._id, selectedSection._id)
                }
            }            
        } finally {
            setMapLoading(false)
        }

    }, [selectedSection])

    async function loadAllSectionSeats(show_uid, location_uid) {
        let cart_uid = null
        try {
            const cart = await getCart()
            if (cart) cart_uid = cart._id
        } catch(e) {} finally {}

        // Create array of promises for parallel loading
        // Skip OPEN sections as they don't have seats to load
        const sectionsNeedingSeats = selectedLocation.sections.filter(section => section.type !== 'OPEN');
        
        const seatPromises = sectionsNeedingSeats.map(async (section) => {
            let payload = {
                location: location_uid,
                section: section._id,
                cart: cart_uid
            }
            
            try {
                const seats = await API.post("vivelo", `/shows/${show_uid}/map/seats`, 
                    { body: payload }
                );
                
                // Add section_uid to each seat
                const seatsWithSection = seats ? seats.map(seat => ({
                    ...seat,
                    section_uid: section._id
                })) : [];
                
                return {
                    sectionId: section._id,
                    seats: seatsWithSection,
                    success: true
                };
            } catch (error) {
                console.error(`Error loading seats for section ${section._id}:`, error)
                return {
                    sectionId: section._id,
                    seats: [],
                    success: false,
                    error: error
                };
            }
        });

        try {
            // Wait for all promises to resolve
            const results = await Promise.all(seatPromises);
            
            const allSeats = []
            const sectionSeatsMap = {}

            // Process results and update state
            results.forEach(result => {
                const { sectionId, seats, success } = result;
                
                if (success && seats.length > 0) {
                    allSeats.push(...seats)
                    sectionSeatsMap[sectionId] = seats
                } else {
                    sectionSeatsMap[sectionId] = []
                }
            });
            
            // Initialize OPEN sections with empty arrays since they don't have seats
            selectedLocation.sections.forEach(section => {
                if (section.type === 'OPEN' && !sectionSeatsMap[section._id]) {
                    sectionSeatsMap[section._id] = [];
                }
            });

            setShowMapSeats(allSeats)
            setSectionSeats(sectionSeatsMap)
            
            // Set selected seats from all sections
            const selected = []
            allSeats.forEach(s => {
                if (s.reserved) {
                    selected.push(s)
                }
            })
            setSelectedSeats(selected)
            
            // Only now set mapLoading to false - all seats are loaded
            setMapLoading(false)
            
        } catch (error) {
            console.error('Error loading all section seats:', error)
            setMapLoading(false) // Set to false even on error to prevent infinite loading
        }
    }
    
    async function loadShowMapSeats(show_uid, location_uid, section_uid) {
        let cart_uid = null
        try {
            const cart = await getCart()
            if (cart) cart_uid = cart._id
        } catch(e) {} finally {}

        let payload = {
            location: location_uid,
            section: section_uid,
            cart: cart_uid
        }
        const seats = await API.post("vivelo", `/shows/${show_uid}/map/seats`, 
            { body: payload }
        );
        
        // Add section_uid to each seat
        const seatsWithSection = seats ? seats.map(seat => ({
            ...seat,
            section_uid: section_uid
        })) : [];
        
        setShowMapSeats(seatsWithSection)
        const selected = []
        seatsWithSection.forEach(s => {
            if (s.reserved) {
                selected.push(s)
            }
        })
        setSelectedSeats(selected)
    }

    async function registerCartItem(ticket, seat, addToCartOnly, quantity = null) {
        trackEvent({
            action: "Clicked Seat Reservation",
            category: "PurchaseFunnel"
        });
        
        const currency = show?.settings?.currency || 'GTQ'        
        const date = moment(show.show_date).format('ddd, LL - LT')
        let payment_type = 'CREDIT_CARD'

        // Get section information from seat's data attributes or find it from the location
        let sectionInfo = null;
        if (seat && seat.section_uid) {
            // If seat has section_uid, use it
            sectionInfo = selectedLocation.sections.find(s => s._id === seat.section_uid);
        } else if (selectedSection) {
            // Fallback to selectedSection for single section layouts
            sectionInfo = selectedSection;
        } else if (seat) {
            // Find section by seat coordinates in grid layout
            for (const section of selectedLocation.sections) {
                const sectionSeatsList = sectionSeats[section._id] || [];
                const foundSeat = sectionSeatsList.find(s => s._id === seat._id);
                if (foundSeat) {
                    sectionInfo = section;
                    break;
                }
            }
        } else {
            // For OPEN sections without seats, use the first available section from the location
            // This is a fallback for grid layouts where we need to specify a section
            sectionInfo = selectedLocation.sections.find(s => s.type === 'OPEN') || selectedLocation.sections[0];
        }

        if (!sectionInfo) {
            console.error('Could not find section information for seat:', seat);
            return;
        }

        const cartItem = {
            ticket_uid: ticket._id,
            type: 'TICKET',                
            show_uid: show._id,
            show_name: show.fullname,
            show_date: show.show_date,
            show_type: show.type,
            venue: show.venue,                
            title: `${show.fullname} (${date})`,
            description: ticket.fullname,
            note: ticket.note,
            ticket_type: ticket.type,
            image: `${show.main_image}`,
            quantity: seat ? 1 : (quantity !== null ? quantity : parseInt(openTicketQuantity)),
            location: {
                location_uid: selectedLocation._id,
                location: selectedLocation.name,
                section_uid: sectionInfo._id,
                section: sectionInfo.name,
                door: sectionInfo.door || 'Unica'
            },
            
        }
        if (seat) {
            cartItem.location.row = seat.row_name
            cartItem.location.seat_uid = seat._id
            cartItem.location.seat = seat.name
            
            cartItem.seat = {
                _id: seat._id,
                location_uid: selectedLocation._id,
                section_uid: sectionInfo._id
            }
        }
        console.log('cartItem', cartItem)
        console.log('items', ticket.bundle)
        if (ticket.bundle && ticket.bundle.length > 0) {
            cartItem.bundle_items = ticket.bundle.map(item => {
                const it = {
                    ticket_uid: item._id,
                    code: item.code,
                    type: 'TICKET',
                    show_uid: show._id,
                    show_name: show.fullname,
                    show_date: show.show_date,
                    show_type: show.type,
                    venue: show.venue,
                    description: item.fullname,
                    note: item.note,
                    ticket_type: item.type,
                    image: `${show.main_image}`,
                    quantity: item.ammount * 1
                }
                if (item.location) {
                    it.location = item.location
                }
                return it
            })
        }

        const items = [cartItem]
        
        try {
            if (seat) {
                seat.reserved = true
                seat.available = false
                seat.show_ticket_uid = ticket._id
            }

            if (paymentType == 'DEPOSIT') {
                if (ticket.payment_type.includes('ACCOUNT_BANK_DEPOSIT')) {
                    payment_type = 'ACCOUNT_BANK_DEPOSIT'
                } else if (ticket.payment_type.includes('BANK_DEPOSIT')) {
                    payment_type = 'BANK_DEPOSIT'
                }
            }

            const cart = await addCartItems({
                payment_type: payment_type, 
                payment_provider: show?.settings?.payment_provider,
                currency: currency, 
                items: items
            })
            updateCartCounter()
            if (addToCartOnly) {
                if (seat) {
                    Notification('success', `El asiento ${seat.name} fue agregado a tu carrito`)
                }
                // Note: OPEN section notifications are handled in their specific handlers
            }
        } catch (error) {
            console.error('Error adding to cart:', error)
            if (seat) {
                seat.reserved = false
                seat.available = true
                delete seat.show_ticket_uid
            }
            Notification('error', 'No fue posible agregar al carrito', null, 'top')
        }
    }
    

    async function removeCartSeat(seat) {
        try {
            const cart = await removeCartSeats({ seats: [seat._id] })
            updateCartCounter()

            seat.reserved = false
            seat.available = true

            if (seat) {
                Notification('success', `El asiento ${seat.name} fue removido de tu carrito`)
            }
                       
        } catch(e) {
            if (e?.response?.status == 400) {                
                Notification('error', 'No fue posible remover los boletos del carrito')
                // history.push('/cart',{})
            } else {
                Notification('error', 'No fue posible remover los boletos del carrito')
                // history.push('/cart',{})
            }
        }
    }
 
    function applyPriceRule(t) {
        console.log('RULE',t)
        if (t.price_rules && t.price_rules.length > 0 && t.price_rules[0].rule_type == 'quantity_discount') {
            const rule = t.price_rules[0]
            if (t.selected >= rule.quantity_min) {
                const discount_price = rule.item_discount_price
                const subtotal_price = discount_price > 0 ? t.selected * discount_price : 0
                return subtotal_price
            }
        }
        return 0
    }

    function calculateTotals() {
        console.log('calculateTotals called with:', { 
            selectedSeats: selectedSeats.length, 
            openTicketQuantity, 
            sectionQuantities 
        });
        
        let total = 0
        let discount_total = 0
        let count = 0
        let currency = show?.settings?.currency || 'GTQ'

        
        let updated = selectedSeats.map(s => {
            // const ticket = showTickets.find(t => t._id == selectedSection.show_ticket_uid)
            const  ticket = selectedTicket
            if (ticket.currency )
            total += ticket.currency == 'USD' ? ticket.price_usd : ticket.price 
            count += ticket.selected
            currency = ticket.currency || show?.settings?.currency || 'GTQ'

            // total += ticket.fee

            discount_total = applyPriceRule(ticket)
            
            return ticket
        })
        // Note: Regular OPEN section totals are now calculated in the grid section loop below
        
        // Calculate totals for all OPEN sections in grid
        if (selectedLocation && selectedLocation.sections) {
            console.log('Checking grid OPEN sections:', selectedLocation.sections.filter(s => s.type === 'OPEN'));
            selectedLocation.sections.forEach(section => {
                // Check both sectionQuantities and openTicketQuantity for grid sections
                const sectionQuantity = sectionQuantities[section._id] || 0;
                const isGridSection = section.section_grid_row !== undefined && section.section_grid_col !== undefined;
                
                console.log('Section check:', { 
                    sectionId: section._id, 
                    sectionQuantity, 
                    openTicketQuantity, 
                    isGridSection,
                    sectionType: section.type 
                });
                
                if (section.type === 'OPEN' && (sectionQuantity > 0 || parseInt(openTicketQuantity) > 0)) {
                    console.log('Processing OPEN section:', { 
                        sectionId: section._id, 
                        sectionQuantity, 
                        openTicketQuantity,
                        isGridSection 
                    });
                    
                    // Find the ticket for this section
                    let sectionTicket = null;
                    if (selectedLocation.show_tickets && selectedLocation.show_tickets.length > 0) {
                        const ticketId = selectedLocation.show_tickets[0];
                        sectionTicket = showTickets.find(t => t._id === ticketId);
                    }
                    
                    if (sectionTicket) {
                        // Use sectionQuantity if available, otherwise fall back to openTicketQuantity
                        const quantity = sectionQuantity > 0 ? sectionQuantity : parseInt(openTicketQuantity);
                        const sectionTotal = (sectionTicket.currency == 'USD' ? sectionTicket.price_usd : sectionTicket.price) * quantity;
                        console.log('Section total:', { 
                            sectionId: section._id, 
                            quantity, 
                            sectionTotal, 
                            ticketPrice: sectionTicket.currency == 'USD' ? sectionTicket.price_usd : sectionTicket.price,
                            isGridSection 
                        });
                        total += sectionTotal;
                        count += quantity;
                        currency = sectionTicket.currency || show?.settings?.currency || 'GTQ';
                        discount_total = applyPriceRule(sectionTicket);
                    }
                }
            });
        }
        console.log('Final totals:', { total, discount_total, currency, count });
        setOrderTotal(total)
        setOrderDiscountTotal(discount_total)        
        setCurrency(currency)
        
    } 

    
    async function onSeatClick(seat, selected) {
        try {
            if (readOnly) return;

            if (seat.waiting) {
                console.log('double click')
                return
            }

            seat.waiting =  true

            if (selected) {
                // const ticket = showTickets.find(t => t._id == selectedTicket._id)
                const ticket = selectedTicket
                console.log('Seat',seat)
                await registerCartItem(ticket, seat, true)

                setSelectedSeats(prevState => {
                    return [...prevState, seat]
                })
            } else {
                // removeItem
                await removeCartSeat(seat)

                setSelectedSeats(prevState => {
                    console.log(seat, selected, prevState)
                    const items = [...prevState]
                    const r = items.filter(s => { return s.row != seat.row || s.col != seat.col })
                        return r
                    })
            }
        } finally {            
            seat.waiting = false
        }
    }

    function renderSelected() {
        const items = selectedSeats.map(s => {
            return <div>{s.name}</div>
        })
        return <div>
            {items}
        </div>
    }


    function renderPlaceholder() {
        return (
            <div className="grid-placeholder">
                <div className="placeholder-content">
                    <span className="placeholder-text"></span>
                </div>
            </div>
        );
    }

    // --- Updated renderSection logic using unified grid system ---
    function renderSection() {
        if (!selectedLocation) return null;
        
        if (shouldUseGridLayout(selectedLocation)) {
            return renderUnifiedSectionGrid(selectedLocation, selectedLocation.sections, {
                readOnly,
                onSeatClick,
                handleSeatMouseEnter,
                handleSeatMouseLeave
            });
            } else if (selectedSection) {
            return renderSectionContent(selectedSection, {
                readOnly,
                onSeatClick,
                handleSeatMouseEnter,
                handleSeatMouseLeave
            });
        }
        
        return null;
    }

    function handleOpenTicketSelection(e, ticket) {
        setOpenTicketQuantity(e.target.value)
    }

    function handleSectionOpenTicketSelection(sectionId, e, ticket) {
        if (!e || !e.target) {
            console.error('Invalid event object in handleSectionOpenTicketSelection:', e);
            return;
        }
        
        const value = e.target.value;
        if (value === undefined || value === null) {
            console.error('Invalid value in handleSectionOpenTicketSelection:', value);
            return;
        }
        
        const newQuantity = parseInt(value) || 0;
        console.log('Updating section quantity:', { sectionId, newQuantity });
        
        setSectionQuantities(prev => {
            const updated = {
                ...prev,
                [sectionId]: newQuantity
            };
            console.log('Updated sectionQuantities:', updated);
            return updated;
        });
    }
    async function handleCheckoutButton() {
        if (isLoading) return;
        setIsLoading(true);
        try {
            history.push('/cart');
        } finally {
            setIsLoading(false);
        }
    }
    async function handleAddToCartButton() {
        const ticket = showTickets.find(t => t._id == selectedTicket._id)
        await registerCartItem(ticket, null, false)
        
        // Show confirmation toast and redirect to cart for OPEN sections
        const sectionName = selectedSection?.name || 'Asiento: no numerado';
        Notification('success', `${openTicketQuantity} tickets en sección ${sectionName} fueron agregados a tu carrito`);
        history.push('/cart');
    }

    async function handleSectionAddToCartButton(sectionId, ticket) {
        const quantity = sectionQuantities[sectionId] || 0
        if (quantity > 0) {
            // Find the section for this sectionId
            const section = selectedLocation.sections.find(s => s._id === sectionId);
            if (section) {
                // Temporarily set selectedSection to the specific section for this cart item
                const originalSelectedSection = selectedSection;
                setSelectedSection(section);
                
                // Add to cart
                await registerCartItem(ticket, null, false, quantity);
                
                // Restore original selectedSection
                setSelectedSection(originalSelectedSection);
                
                // Show confirmation toast and redirect to cart for OPEN sections in grid
                const sectionName = section.name || 'Asiento: no numerado';
                Notification('success', `${quantity} tickets en sección ${sectionName} fueron agregados a tu carrito`);
                history.push('/cart');
            }
        }
    }
    function validateOpenTicketSelection() {
        return openTicketQuantity > 0
    }

    function validateSectionOpenTicketSelection(sectionId) {
        return (sectionQuantities[sectionId] || 0) > 0
    }


    function renderVenueLegend() {
        return (
            <div className="venue-legend">
                <div className="venue-legend-item">
                    <div className="seat available"></div>
                    <span>Disponible</span>
                </div>
                <div className="venue-legend-item">
                    <div className="seat reserved"></div>
                    <span>Reservado</span>
                </div>
                <div className="venue-legend-item">
                    <div className="seat sold"></div>
                    <span>No disponible</span>
                </div>
            </div>
        );
    }







    function onLocationSelected(e) {        
        // console.log(showMap.locations, e.target.value)
        
        const [location_uid, ticket_uid] = e.target.value.split('-')
        const location = showMap.locations.find(l => l._id == location_uid)
        const ticket = showTickets.find(t => t._id == ticket_uid)
        
        setSelectedSeats([])
        setSelectedSection(null)
        setSelectedLocation(location)
        setSelectedTicket(ticket)

        if (location && location.sections.length == 1) {
            setSelectedSection(location.sections[0])
        } else {
            setSelectedSection(null)
        }
    }

    function renderLocationSelect() {
        const locationOptions = [
            <option key="location_select">-- Selecciona una localidad --</option>
        ]
        
        showMap.locations
            .filter(l => l.available )
            .map(l => {  
                if (readOnly) {
                    locationOptions.push(<option key={`${l._id}`} value={`${l._id}`}>{l.name}</option>)
                } else {
                    l.show_tickets.map(lt => {
                        const ticket = showTickets.find(t => {                
                            return t._id == lt
                        })
                        if (ticket) {
                            const price = isRegistrationTicket ? "Acceso gratuito" : (ticket.currency == 'USD' ? ticket.price_usd_text : ticket.price_text)
                            const name = `${l.name} - (${ticket.fullname} ${price})`
                            locationOptions.push(<option key={`${l._id}-${ticket._id}`} value={`${l._id}-${ticket._id}`}>{name}</option>)
                        }
                    })          
                }
            })

        const selectedValue = readOnly ? selectedLocation?._id : `${selectedLocation?._id}-${selectedTicket?._id}`
        return <div>
                <Form.Control 
                    as="select" 
                    value={selectedValue} 
                    onChange={onLocationSelected}
                    className="form-control-theme"
                >
                    {locationOptions}
                </Form.Control>
            </div>
    }

    function onSectionSelected(e) {
        const section = selectedLocation.sections.find(s => s._id == e.target.value)
        setSelectedSeats([])
        setSelectedSection(section)
    }

    function renderSectionsSelect() {
        const sectionOptions = [
            <option key="section_select" value="">-- Selecciona una seccion --</option>
        ]
        
        selectedLocation.sections
            .filter(s => s.available )
            .map(s => {
                sectionOptions.push(<option key={s._id} value={s._id}>{s.name}</option>)
            })

        return <div>                
                <Form.Control 
                    as="select" 
                    value={selectedSection ? selectedSection._id : ''} 
                    onChange={onSectionSelected}
                    className="form-control-theme"
                >
                    {sectionOptions}
                </Form.Control>
            </div>
    }

    function validateTickets() {
        return selectedSeats.length > 0
    }

    function renderTotal() {
        if (isRegistrationTicket) {
            return ""; // Don't show total for registration tickets
        }
        
        if (orderDiscountTotal > 0) {
            return <><span style={{textDecoration:'line-through'}}>{currency} {orderTotal}</span> {currency} {orderDiscountTotal}</>
        } else {
            return <>{currency} {orderTotal}</>
        }

    }
    
    function renderMapImage() {
        let imgsrc = showMap.map_image
        if (selectedLocation && selectedLocation.map_image) {
            imgsrc = selectedLocation.map_image 
        }

        return <div className="venue_map_image">
            <img style={{width:'100%'}} src={imgsrc} />
        </div>
    }

    function render() {            
        console.log('showTickets', showTickets)

        return showMap && showTickets && (
            <div className="venue_map_selection">
                <SeatTooltip 
                    seat={tooltipState.seat}
                    section={tooltipState.section}
                    location={selectedLocation}
                    isVisible={tooltipState.isVisible}
                    position={tooltipState.position}
                />
            {renderLocationSelect()}
            {selectedLocation && selectedLocation.sections.length > 1 && showMapSeats && 
                !shouldUseGridLayout(selectedLocation) &&
                renderSectionsSelect()
            }
            {renderSection()}            

            {!isRegistrationTicket && (
            <div className="tickets-total" style={{marginTop: 20,textAlign: 'center'}}>
                <div style={{textAlign: 'center'}}>TOTAL {renderTotal()}</div>
                </div>
            )}
                { showViewCart && 
                    <div style={{textAlign: 'center', display: 'flex', justifyContent: 'center'}}>
                        <LoaderButton 
                            className="btn-theme-primary" 
                            style={{width:200}}                          
                            onClick={handleCheckoutButton}
                            isLoading={isLoading}
                        >
                            <FontAwesomeIcon icon={faShoppingCart} /> Ver Carrito
                        </LoaderButton>
                    </div>                    
                }
            { renderMapImage() }
        </div>
        )
    }

    // Tooltip handlers
    const handleSeatMouseEnter = (event, seat, section) => {
        const rect = event.currentTarget.getBoundingClientRect();
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        
        // Calculate available space in each direction
        const spaceAbove = rect.top;
        const spaceBelow = viewportHeight - rect.bottom;
        const spaceLeft = rect.left;
        const spaceRight = viewportWidth - rect.right;
        
        // Tooltip dimensions (approximate)
        const tooltipWidth = 200;
        const tooltipHeight = 80;
        
        let tooltipX, tooltipY;
        
        // Prefer showing tooltip to the right if there's enough space
        if (spaceRight >= tooltipWidth + 10) {
            tooltipX = rect.right + 10;
            tooltipY = rect.top + rect.height / 2 - tooltipHeight / 2;
        }
        // Otherwise show to the left if there's enough space
        else if (spaceLeft >= tooltipWidth + 10) {
            tooltipX = rect.left - tooltipWidth - 10;
            tooltipY = rect.top + rect.height / 2 - tooltipHeight / 2;
        }
        // Otherwise show above if there's enough space
        else if (spaceAbove >= tooltipHeight + 10) {
            tooltipX = rect.left + rect.width / 2 - tooltipWidth / 2;
            tooltipY = rect.top - tooltipHeight - 10;
        }
        // Otherwise show below
        else {
            tooltipX = rect.left + rect.width / 2 - tooltipWidth / 2;
            tooltipY = rect.bottom + 10;
        }
        
        // Ensure tooltip stays within viewport bounds
        tooltipX = Math.max(10, Math.min(tooltipX, viewportWidth - tooltipWidth - 10));
        tooltipY = Math.max(10, Math.min(tooltipY, viewportHeight - tooltipHeight - 10));
        
        setTooltipState({
            isVisible: true,
            seat,
            section,
            position: { x: tooltipX, y: tooltipY }
        });
    };

    const handleSeatMouseLeave = () => {
        setTooltipState({
            isVisible: false,
            seat: null,
            section: null,
            position: { x: 0, y: 0 }
        });
    };

    // --- Unified Grid System ---
    
    /**
     * Checks if a location should use grid layout
     * @param {Object} location - The location object
     * @returns {boolean} True if grid layout should be used
     */
    function shouldUseGridLayout(location) {
        return location.section_grid_rows && 
               location.section_grid_cols && 
               location.sections.some(s => s.section_grid_row !== undefined);
    }
    
    /**
     * Renders a unified section grid that works with any section type
     * @param {Object} location - The location object
     * @param {Array} sections - Array of sections
     * @param {Object} options - Rendering options
     * @returns {JSX.Element} The rendered grid
     */
    function renderUnifiedSectionGrid(location, sections, options) {
        const { section_grid_rows, section_grid_cols } = location;
        
        // Check if all sections with grid coordinates have their seats loaded
        const sectionsWithGridCoords = sections.filter(s => 
            s.section_grid_row !== undefined && s.section_grid_col !== undefined
        );
        
        // Filter out sections that are not available or don't have matching show tickets
        const availableSectionsWithGridCoords = sectionsWithGridCoords.filter(section => {
            // Check if section is available
            if (!section.available) {
                return false;
            }
            
            // Check if section has show_tickets defined
            if (!section.show_tickets || section.show_tickets.length === 0) {
                return false;
            }
            
            // Check if location has show_tickets defined
            if (!location.show_tickets || location.show_tickets.length === 0) {
                return false;
            }
            
            // Check if section.show_tickets and location.show_tickets have matching tickets
            const sectionTicketIds = new Set(section.show_tickets);
            const locationTicketIds = new Set(location.show_tickets);
            const matchingTickets = [...sectionTicketIds].filter(id => locationTicketIds.has(id));
            
            if (matchingTickets.length === 0) {
                return false;
            }
            
            // Check if at least one matching ticket is available
            const hasAvailableTickets = matchingTickets.some(ticketId => {
                const ticket = showTickets.find(t => t._id === ticketId);
                return ticket && ticket.real_available > 0;
            });
            
            if (!hasAvailableTickets) {
                return false;
            }
            
            return true;
        });
        
        // For OPEN sections, we don't need to wait for seats to load
        const sectionsNeedingSeats = availableSectionsWithGridCoords.filter(section => section.type !== 'OPEN');
        
        const allSectionsLoaded = sectionsNeedingSeats.every(section => 
            sectionSeats[section._id] !== undefined
        );
        
        if (mapLoading || !allSectionsLoaded) {
            return renderGridLoadingState();
        }
        
        const sectionsMap = new Map();
        availableSectionsWithGridCoords.forEach(section => {
            const key = `${section.section_grid_row}-${section.section_grid_col}`;
            sectionsMap.set(key, section);
        });
        
        const gridCells = [];
        for (let row = 0; row < section_grid_rows; row++) {
            for (let col = 0; col < section_grid_cols; col++) {
                const key = `${row}-${col}`;
                const section = sectionsMap.get(key);
                gridCells.push(
                    <div
                        key={key}
                        className="grid-cell"
                        style={{ gridRow: row + 1, gridColumn: col + 1 }}
                    >
                        {section ? renderSectionContent(section, { ...options, inGrid: true }) : renderPlaceholder()}
                    </div>
                );
            }
        }
        
        return renderGridContainer(gridCells, section_grid_rows, section_grid_cols);
    }
    
    /**
     * Renders the grid container with proper styling
     * @param {Array} gridCells - Array of grid cell elements
     * @param {number} gridRows - Number of grid rows
     * @param {number} gridCols - Number of grid columns
     * @returns {JSX.Element} The grid container
     */
    function renderGridContainer(gridCells, gridRows, gridCols) {
        return (
            <div className="venue_map">
                <div className="venue_section">
                    {renderVenueLegend()}
                    <div
                        className="sections-grid"
                        style={{
                            display: 'grid',
                            gridTemplateRows: `repeat(${gridRows}, 1fr)`,
                            gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
                            gap: '20px 10px',
                            padding: '20px',
                        }}
                    >
                        {gridCells}
                    </div>
                </div>
            </div>
        );
    }
    
    /**
     * Renders the loading state for grid layout
     * @returns {JSX.Element} Loading state component
     */
    function renderGridLoadingState() {
        return (
            <div className="venue_map">
                <div className="venue_section">
                    {renderVenueLegend()}
                    <div className="grid-loading">
                        <div className="loading-spinner"></div>
                        <div className="loading-text">Cargando mapa de asientos...</div>
                    </div>
                </div>
            </div>
        );
    }
    
    /**
     * Renders section content using the registry
     * @param {Object} section - The section object
     * @param {Object} options - Rendering options
     * @returns {JSX.Element} The rendered section content
     */
    function renderSectionContent(section, options) {
        const context = {
            sectionSeats,
            showMapSeats,
            onSeatClick,
            handleSeatMouseEnter,
            handleSeatMouseLeave,
            showTickets,
            selectedTicket,
            openTicketQuantity,
            handleOpenTicketSelection,
            handleAddToCartButton,
            validateOpenTicketSelection,
            sectionQuantities,
            handleSectionOpenTicketSelection,
            handleSectionAddToCartButton,
            validateSectionOpenTicketSelection
        };
        
        const result = renderSectionByType(section, options, context);
        return result;
    }

    return render()
}

// --- Seat Tooltip Component ---
function SeatTooltip({ seat, section, location, isVisible, position }) {
    if (!isVisible || !seat) return null;

    const getStatusText = () => {
        if (seat.waiting) return 'Cargando...';
        if (seat.available) return 'Disponible';
        if (seat.reserved) return 'Reservado';
        return 'No disponible';
    };

    const getStatusColor = () => {
        if (seat.waiting) return '#FFA500';
        if (seat.available) return '#C9167E';
        if (seat.reserved) return '#02D0C6';
        return '#666';
    };

    return (
        <div 
            className="seat-tooltip"
            style={{
                position: 'fixed',
                top: position.y,
                left: position.x,
                zIndex: 10000,
                pointerEvents: 'none'
            }}
        >
            <div className="tooltip-content">
                <div className="tooltip-header">
                    <strong>{section?.name || 'N/A'} - {seat.name}</strong>
                </div>
                <div className="tooltip-body">
                    <div className="tooltip-row">
                        <span className="tooltip-label">Ubicación:</span>
                        <span className="tooltip-value">{location?.name || 'N/A'}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Sección:</span>
                        <span className="tooltip-value">{section?.name || 'N/A'}</span>
                    </div>
                    <div className="tooltip-row">
                        <span className="tooltip-label">Estado:</span>
                        <span className="tooltip-value" style={{color: getStatusColor()}}>
                            {getStatusText()}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}